import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';

import { HeaderLeft } from './header-left';
import { StepStatusSelector } from '../../process/components/step-status-selector';
import { ClipboardTick } from '../../../components/icons';
import { useResponsive } from '../../../hooks/useResponsive';
import { uiStore } from '../../../core/stores/ui-store';
import { Step } from '../../../app/entities/step';
import { TaskPropertyButton } from '../../tasks/components/task-property.button';
import { type PlainDate } from '../../../core/services/plain-date';

interface ScreenHeaderProps {
  stepId?: string;
  title: string;
  dueDate?: PlainDate | null;
  startDate?: PlainDate | null;
  actions?: React.ReactNode[];
  canValidateStep?: boolean;
  breadcrumb?: React.ReactNode;
  menu?: React.ReactNode;
  handleUpdateTimeFrame?: (values: {
    dueDate?: PlainDate | null | undefined;
    startDate?: PlainDate | null | undefined;
  }) => void;
}

export const ScreenHeader: React.FC<ScreenHeaderProps> = observer(
  ({
    stepId,
    title,
    dueDate,
    startDate,
    actions,
    canValidateStep = true,
    breadcrumb,
    menu,
    handleUpdateTimeFrame,
  }) => {
    const { isDesktop } = useResponsive();

    const step = Step.getOne(stepId!)!;

    const renderLeftComponent = () => {
      if (isDesktop && breadcrumb) {
        return breadcrumb;
      }

      return (
        <HeaderLeft
          title={title}
          dueDate={isDesktop ? dueDate : undefined}
          startDate={isDesktop ? startDate : undefined}
          onUpdate={handleUpdateTimeFrame!}
        />
      );
    };

    const handleShowTasksPanel = () => {
      uiStore.isTaskPanelOpen ? uiStore.closeTaskPanel() : uiStore.openTaskPanel(stepId!);
    };

    return (
      <Header>
        {renderLeftComponent()}

        <HeaderDocument>
          {actions && actions.length > 0 && actions.map((action) => action)}

          {isDesktop && <>{canValidateStep && stepId && <StepStatusSelector stepId={stepId} />}</>}

          <TaskPropertyButton
            icon={<ClipboardTick />}
            onClick={handleShowTasksPanel}
            label={`${step.completedTask}/${step.activeTasksCount} Tasks`}
            isActive={true}
          />

          {menu}
        </HeaderDocument>
      </Header>
    );
  },
);

const Header = styled.header`
  height: 4rem;
  min-height: 4rem;
  border-bottom: 1px var(--content-border-color) solid;
  color: #e3e4e7;
  font-size: 1.3rem;
  font-weight: 600;
  padding: 0 1.6rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  justify-content: space-between;
  cursor: default;
`;

const HeaderDocument = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  gap: var(--spacing-gap-sm);
`;
